import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { videos } from "../data/mock";

const VideoCard = ({ v, featured }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    if (!ref.current) return;
    if (playing) {
      ref.current.pause();
      setPlaying(false);
    } else {
      ref.current.play();
      setPlaying(true);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!ref.current) return;
    ref.current.muted = !muted;
    setMuted(!muted);
  };

  const goFs = (e) => {
    e.stopPropagation();
    if (!ref.current) return;
    if (ref.current.requestFullscreen) ref.current.requestFullscreen();
  };

  return (
    <div className={`relative bg-black overflow-hidden group reveal ${featured ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
      <video
        ref={ref}
        src={v.src}
        poster={v.poster}
        muted={muted}
        playsInline
        preload="metadata"
        onClick={toggle}
        onEnded={() => setPlaying(false)}
        className="absolute inset-0 w-full h-full object-cover cursor-pointer"
      />
      {!playing && (
        <div
          onClick={toggle}
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/10 to-black/20 cursor-pointer"
        >
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-[#F5F1EA] text-[#F5F1EA] flex items-center justify-center backdrop-blur-sm bg-white/5 group-hover:bg-[#8B6F47] group-hover:border-[#8B6F47] transition-colors duration-500">
            <Play size={28} className="ml-1" fill="currentColor" />
          </div>
        </div>
      )}

      {/* Top label */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between text-[#F5F1EA]">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">{v.category} · {v.year}</div>
          <div className="font-display text-2xl md:text-3xl mt-1 leading-tight">{v.title}</div>
          <div className="italic-display text-sm md:text-base opacity-80">{v.subtitle}</div>
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">{v.duration}</div>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[#F5F1EA] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <button onClick={toggle} className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-[#8B6F47]">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <div className="flex gap-2">
          <button onClick={toggleMute} className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-[#8B6F47]">
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button onClick={goFs} className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-[#8B6F47]">
            <Maximize2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const VideoWork = () => {
  return (
    <section id="video" className="py-24 md:py-36 bg-[#1A1A1A] text-[#F5F1EA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="reveal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#8B6F47] mb-4">(03) Video Work</div>
            <h2 className="font-display text-[48px] md:text-[76px] leading-[0.98] tracking-tight">
              Stories in <span className="italic-display text-[#C2B8A3]">motion</span>.
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-[#F5F1EA]/70 reveal">
            Documentary films and short-form brand work. Each project begins with listening —
            then finding the frame that tells the truth of it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {videos.map((v, i) => (
            <VideoCard key={v.id} v={v} featured={i === 0} />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-6 text-[#F5F1EA]/70 text-sm reveal">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {["Documentary", "Event Recap", "Teasers", "Social Content"].map(
              (t) => (
                <span key={t} className="italic-display text-lg text-[#F5F1EA]/80">
                  {t}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoWork;
